import { composePostButton } from '../strings.locstring.json';
import { composeDiscardButton } from 'owa-locstrings/lib/strings/composediscardbutton.locstring.json';
import { composeSendButton } from 'owa-locstrings/lib/strings/composesendbutton.locstring.json';
import loc from 'owa-localize';
import DraftSaveTime from './DraftSaveTime';
import { observer } from 'mobx-react';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ControlIcons } from 'owa-control-icons';
import setShowSendDiscardAsIcon from 'owa-mail-compose-actions/lib/actions/setShowSendDiscardAsIcon';
import { ComposeViewState } from 'owa-mail-compose-store';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-actions/lib/utils/createPublicFolderComposeViewState';
import { hasKeybindForCurrentMode } from 'owa-readable-hotkeys/lib/utils/hasKeybindForCurrentMode';
import { convertHotkeyToReadableText } from 'owa-readable-hotkeys/lib/utils/convertHotkeyToReadableText';
import { getFirstHotkey } from 'owa-readable-hotkeys/lib/utils/getFirstHotkey';

import { getCommands } from 'owa-mail-hotkeys';
import * as React from 'react';

const styles = require('./ComposeBottom.scss');
const classNames = require('classnames/bind').bind(styles);

const MIN_WIDTH_FOR_TEXT_BUTTON = 380;

export interface ComposeBottomSendDiscardProps {
    viewState: ComposeViewState;
    composeId: string;
    onSend: () => void;
    onDiscard: () => void;
}

/**
 * Renders the send/discard button and the Drafts of the compose bottom.
 */
@observer
export default class ComposeBottomSendDiscard extends React.Component<
    ComposeBottomSendDiscardProps,
    {}
> {
    private containerRef: React.RefObject<HTMLDivElement> = React.createRef();
    private draftSaveTimeRef: React.RefObject<DraftSaveTime> = React.createRef();

    render() {
        return (
            <div ref={this.containerRef} className={classNames(styles.buttonBar)}>
                {this.renderSendDiscardButton()}
                {React.Children.count(this.props.children) == 0
                    ? this.renderPlaceholderContent()
                    : this.props.children}
                <DraftSaveTime ref={this.draftSaveTimeRef} viewState={this.props.viewState} />
            </div>
        );
    }

    /**
     * When no child is provided, render a placeholder that will grow to the available size
     */
    private renderPlaceholderContent() {
        return <div className={styles.buttonBarPlaceholder} />;
    }

    private renderSendDiscardButton() {
        const { viewState, onSend, onDiscard } = this.props;
        const sendClassName = classNames(styles.sendButton, {
            iconButton: viewState.showSendDiscardAsIcon,
        });
        const discardClassName = classNames(styles.discardButton, {
            iconButton: viewState.showSendDiscardAsIcon,
        });

        const sendLabel: string = isPublicFolderComposeViewState(viewState)
            ? loc(composePostButton)
            : loc(composeSendButton);

        // The actual displayed title text includes both the action command and the
        // readable hotkey command
        const sendHotkey = getFirstHotkey(getCommands().sendMail);
        const discardHotkey = getFirstHotkey(getCommands().discardDraft);
        const sendTitle = hasKeybindForCurrentMode(sendHotkey)
            ? `${sendLabel} (${convertHotkeyToReadableText(sendHotkey)})`
            : sendLabel;
        const discardTitle = hasKeybindForCurrentMode(discardHotkey)
            ? `${loc(composeDiscardButton)} (${convertHotkeyToReadableText(discardHotkey)})`
            : loc(composeDiscardButton);

        return (
            <div className={styles.sendDiscard}>
                <PrimaryButton
                    className={sendClassName}
                    onClick={onSend}
                    ariaLabel={sendLabel}
                    disabled={viewState.sendDisabled}
                    title={sendTitle}>
                    {viewState.showSendDiscardAsIcon ? (
                        <Icon iconName={ControlIcons.Send} />
                    ) : (
                        sendLabel
                    )}
                </PrimaryButton>
                <DefaultButton
                    className={discardClassName}
                    onClick={onDiscard}
                    ariaLabel={loc(composeDiscardButton)}
                    title={discardTitle}>
                    {viewState.showSendDiscardAsIcon ? (
                        <Icon iconName={ControlIcons.Delete} />
                    ) : (
                        loc(composeDiscardButton)
                    )}
                </DefaultButton>
            </div>
        );
    }

    resize() {
        if (this.containerRef.current && this.draftSaveTimeRef.current) {
            const { viewState } = this.props;

            const availableWidth =
                this.containerRef.current.clientWidth - this.draftSaveTimeRef.current.getWidth();

            // Calculate showSendDiscardAsIcon with the toolbarWidth and update the viewState if dirty
            const showSendDiscardAsIcon: boolean = availableWidth < MIN_WIDTH_FOR_TEXT_BUTTON;
            if (showSendDiscardAsIcon != viewState.showSendDiscardAsIcon) {
                setShowSendDiscardAsIcon(viewState, showSendDiscardAsIcon);
            }
        }
    }
}
